import { __decorate, __param } from '../../../../../../node_modules/tslib/tslib.es6.js';
import { DEFAULT_EDITOR_ASSOCIATION, findViewStateForEditor, isUntitledResourceEditorInput } from '../../../common/editor.js';
import { AbstractTextResourceEditorInput } from '../../../common/editor/textResourceEditorInput.js';
import { ITextFileService } from '../../textfile/common/textfiles.js';
import { ILabelService } from 'monaco-editor/esm/vs/platform/label/common/label.js';
import { IEditorService } from '../../editor/common/editorService.js';
import { IFileService } from 'monaco-editor/esm/vs/platform/files/common/files.js';
import { toLocalResource, isEqual } from 'monaco-editor/esm/vs/base/common/resources.js';
import { IWorkbenchEnvironmentService } from '../../environment/common/environmentService.js';
import { IPathService } from '../../path/common/pathService.js';
let UntitledTextEditorInput = class UntitledTextEditorInput extends AbstractTextResourceEditorInput {
    get typeId() {
        return UntitledTextEditorInput.ID;
    }
    get editorId() {
        return DEFAULT_EDITOR_ASSOCIATION.id;
    }
    constructor(model, textFileService, labelService, editorService, fileService, environmentService, pathService) {
        super(model.resource, undefined, editorService, textFileService, labelService, fileService);
        this.model = model;
        this.environmentService = environmentService;
        this.pathService = pathService;
        this.modelResolve = undefined;
        this.registerModelListeners(model);
    }
    registerModelListeners(model) {
        this._register(model.onDidChangeDirty(() => this._onDidChangeDirty.fire()));
        this._register(model.onDidChangeName(() => this._onDidChangeLabel.fire()));
        this._register(model.onDidRevert(() => this.dispose()));
    }
    getName() {
        return this.model.name;
    }
    getDescription(verbosity = 1 ) {
        if (!this.model.hasAssociatedFilePath) {
            const descriptionCandidate = this.resource.path;
            if (descriptionCandidate !== this.getName()) {
                return descriptionCandidate;
            }
            return undefined;
        }
        return super.getDescription(verbosity);
    }
    getTitle(verbosity) {
        if (!this.model.hasAssociatedFilePath) {
            const name = this.getName();
            const description = this.getDescription();
            if (description && description !== name) {
                return `${name} • ${description}`;
            }
            return name;
        }
        return super.getTitle(verbosity);
    }
    isDirty() {
        return this.model.isDirty();
    }
    getEncoding() {
        return this.model.getEncoding();
    }
    setEncoding(encoding, mode ) {
        return this.model.setEncoding(encoding);
    }
    setLanguageId(languageId, source) {
        this.model.setLanguageId(languageId, source);
    }
    getLanguageId() {
        return this.model.getLanguageId();
    }
    async resolve() {
        if (!this.modelResolve) {
            this.modelResolve = this.model.resolve();
        }
        await this.modelResolve;
        return this.model;
    }
    toUntyped(options) {
        const untypedInput = {
            resource: this.model.hasAssociatedFilePath ? toLocalResource(this.model.resource, this.environmentService.remoteAuthority, this.pathService.defaultUriScheme) : this.resource,
            forceUntitled: true,
            options: {
                override: this.editorId
            }
        };
        if (typeof options?.preserveViewState === 'number') {
            untypedInput.encoding = this.getEncoding();
            untypedInput.languageId = this.getLanguageId();
            untypedInput.contents = this.model.isDirty() ? this.model.textEditorModel?.getValue() : undefined;
            untypedInput.options.viewState = findViewStateForEditor(this, options.preserveViewState, this.editorService);
            if (typeof untypedInput.contents === 'string' && !this.model.hasAssociatedFilePath) {
                untypedInput.resource = undefined;
            }
        }
        return untypedInput;
    }
    matches(otherInput) {
        if (this === otherInput) {
            return true;
        }
        if (otherInput instanceof UntitledTextEditorInput) {
            return isEqual(otherInput.resource, this.resource);
        }
        if (isUntitledResourceEditorInput(otherInput)) {
            return super.matches(otherInput);
        }
        return false;
    }
    dispose() {
        this.modelResolve = undefined;
        super.dispose();
    }
};
UntitledTextEditorInput.ID = 'workbench.editors.untitledEditorInput';
UntitledTextEditorInput = ( __decorate([
    ( __param(1, ITextFileService)),
    ( __param(2, ILabelService)),
    ( __param(3, IEditorService)),
    ( __param(4, IFileService)),
    ( __param(5, IWorkbenchEnvironmentService)),
    ( __param(6, IPathService))
], UntitledTextEditorInput));
export { UntitledTextEditorInput };
